import React from "react";

//simple
import C_V_TEXT from "../c_view/C_V_TEXT";
import C_V_FORMULA from "../c_view/C_V_FORMULA";
import C_V_F_TEXT from "../c_view/C_V_F_TEXT";

//complex
import C_V_IMG from "../c_view/C_V_IMG";
import C_V_SELECT from "../c_view/C_V_SELECT";
import C_V_MULTI from "../c_view/C_V_MULTI";
import C_V_CITY from "../c_view/C_V_CITY";
import C_V_ADDRESS from "../c_view/C_V_ADDRESS";
import C_V_DOC from "../c_view/C_V_DOC";
import C_V_USER from "../c_view/C_V_USER";
import C_V_LATLNG from "../c_view/C_V_LATLNG";
import C_V_TABLE from "../c_view/C_V_TABLE";

//textarea
import C_V_TEXTAREA from "../c_view/C_V_TEXTAREA";
import C_E_TEXTAREA from "../c_edit/C_E_TEXTAREA";

//number
import C_V_NUMBER from "../c_view/C_V_NUMBER";
import C_E_NUMBER from "../c_edit/C_E_NUMBER";

//range
import C_V_RANGE from "../c_view/C_V_RANGE";
import C_E_RANGE from "../c_edit/C_E_RANGE";

//phone_number
import C_V_PHONE from "../c_view/C_V_PHONE";
import C_E_PHONE from "../c_edit/C_E_PHONE";

//year
import C_V_YEAR from "../c_view/C_V_YEAR";
import C_E_YEAR from "../c_edit/C_E_YEAR";

//date
import C_V_DATE from "../c_view/C_V_DATE";
import C_E_DATE from "../c_edit/C_E_DATE";
import C_V_A_DATE from "../c_view/C_V_A_DATE";

//time
import C_V_TIME from "../c_view/C_V_TIME";
import C_E_TIME from "../c_edit/C_E_TIME";

//link
import C_V_LINK from "../c_view/C_V_LINK";
import C_E_LINK from "../c_edit/C_E_LINK";

const VirtualTableRow = React.memo(
  ({
    row,
    absoluteRow,
    visualRowNumber,
    visibleFields,
    totalWidth,
    activeCol,
    editingField,
    tempValue,
    isFeatureSelected,
    rowHeight,
    editable,
    isViewer,
    onExpandClick,
    onContextMenu,
    setCellRef,
    handleChange,
    handleBlur,
    handleKeyDown,
    stopEdit,
    handleSaveData,
  }) => {
    const rowKey = row.id || row.key || absoluteRow;

    return (
      <div
        role="row"
        id={row.id}
        key={rowKey}
        data-key={rowKey}
        className={`table_body_row ${isFeatureSelected ? "row_selected" : ""}`}
        style={{ position: "relative", width: totalWidth, height: rowHeight }}
        onContextMenu={(e) => onContextMenu(e, row)}
      >
        <div
          role="cell"
          className="table_body_no_col"
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            height: "100%",
            width: 70,
            margin: 0,
          }}
        >
          <button
            className="button_icon background_grey margin_right"
            id="expand_menu"
            onClick={(e) => onExpandClick(e, row)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#000"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="table_body_svg_icon"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          {visualRowNumber}
        </div>

        {visibleFields.map((field) => {
          const cIndex = field.colIndex;
          const isSelected = activeCol === cIndex;
          const value = row?.properties?.[field.key];
          const isEditing = editingField === field.key;
          const isSection = field.type === "section";

          // Guard for System, Computed, and Modal-only field types
          const nonClickableTypes = [
            "section",
            "math_operators",
            "rt",
            "rw",
            "counting_poi",
            "counting_custom",
            "calculate_dimension",
            "simple_referring",
            "2d_referring",
            "conditional_statement",
            "table_formula",
            "id_data",
            "last_edited",
            "edited_by",
            "template_literal",
            "table",
            "sheet",
          ];

          const isCellClickable =
            editable && !isViewer && !nonClickableTypes.includes(field.type);

          return (
            <div
              role="cell"
              tabIndex={0}
              key={field.key}
              className="table_body_data_col"
              data-row-id={row.id}
              data-abs-row={absoluteRow}
              data-col-idx={cIndex}
              data-field-key={field.key}
              style={{
                userSelect: "none", // BROWSER UI OPTIMIZATION: Prevent accidental text highlighting during rapid double clicks
                position: "absolute",
                left: field.left,
                top: 0,
                height: "100%",
                width: field.width,
                cursor: isCellClickable ? "pointer" : "default",
                overflow: field.type === "phone_number" ? "visible" : "hidden",
                margin: 0,
                // Using Alpha Blending to allow CSS hover states to shine through
                backgroundColor: isSelected
                  ? "rgba(102, 102, 255, 0.1)" // Cell is actively focused
                  : isSection
                    ? "rgba(0, 0, 0, 0.04)" // Looks like #f5f5f5 but blends with row hover!
                    : "transparent",
                boxShadow: isSelected
                  ? "inset 0 0 0 2px #6666ff, inset 0 0 6px rgba(102, 102, 255, 0.4)"
                  : "none",
                // Overriding borders makes it look like a seamless colspan
                borderTop: isSection ? "none" : undefined,
                borderBottom: isSection ? "none" : undefined,
                borderLeft: isSection ? "none" : undefined,
                borderRight: isSection ? "none" : undefined,
              }}
              ref={setCellRef(absoluteRow, cIndex)}
              onKeyDown={(e) => {
                // Check for CTRL+C (Windows/Linux) or CMD+C (Mac)
                if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "c") {
                  // Only execute if not currently editing (to allow normal text copy during edit)
                  if (!isEditing) {
                    e.preventDefault(); // Prevent default browser behavior

                    let textToCopy = "";
                    if (value !== null && value !== undefined) {
                      // If the raw value is an object or array, stringify it
                      textToCopy =
                        typeof value === "object"
                          ? JSON.stringify(value)
                          : String(value);
                    }

                    navigator.clipboard
                      .writeText(textToCopy)
                      .catch((err) =>
                        console.error("Failed to copy cell value:", err),
                      );
                  }
                }
              }}
            >
              {isSection ? null : field?.type === "textarea" ? (
                isEditing ? (
                  <C_E_TEXTAREA
                    tempValue={tempValue}
                    rowId={row.id}
                    fieldKey={field.key}
                    onChange={handleChange}
                    onBlur={() => handleBlur(row.id, field.key, value)}
                    onKeyDown={(e) =>
                      handleKeyDown(e, row.id, field.key, value)
                    }
                  />
                ) : (
                  <C_V_TEXTAREA value={value} />
                )
              ) : field?.type === "selection" ||
                field?.type === "radio_button" ? (
                <C_V_SELECT
                  value={value}
                  isEditing={isEditing}
                  isViewer={isViewer}
                  isAllowOtherAnswer={field.isAllowOtherAnswer}
                  field={field}
                  row={row}
                />
              ) : field?.type === "checklist" ? (
                <C_V_MULTI
                  value={value}
                  isEditing={isEditing}
                  isViewer={isViewer}
                  isAllowOtherAnswer={field.isAllowOtherAnswer}
                  field={field}
                  row={row}
                />
              ) : field?.type === "image" ? (
                <C_V_IMG
                  value={value}
                  isEditing={isEditing}
                  row={row}
                  field={field}
                  stopEdit={stopEdit}
                />
              ) : field?.type === "document" ? (
                <C_V_DOC value={value} />
              ) : field?.type === "table" ? (
                /* <-- THE NEW C_V_TABLE RENDER BLOCK --> */
                <C_V_TABLE
                  row={row}
                  field={field}
                  child_array={row?.child_array}
                />
              ) : ["latitude", "longitude"].includes(field?.type) ? (
                <C_V_LATLNG
                  value={
                    field.type === "longitude"
                      ? (row?.geometry?.coordinates?.[0] ?? value)
                      : (row?.geometry?.coordinates?.[1] ?? value)
                  }
                />
              ) : field?.type === "auto_generate_user_name" ? (
                <C_V_USER value={value} user={row.user} />
              ) : field?.type === "edited_by" ? (
                <C_V_USER value={value} user={row.updated_by} />
              ) : [
                  "counting_poi",
                  "counting_custom",
                  "calculate_dimension",
                  "simple_referring",
                  "2d_referring",
                  "math_operators",
                  "rt",
                  "rw",
                  "conditional_statement",
                  "table_formula",
                  "template_literal",
                ].includes(field?.type) ? (
                <C_V_FORMULA value={value} />
              ) : field?.type === "id_data" ? (
                <C_V_F_TEXT value={row?._id} />
              ) : field?.type === "last_edited" ? (
                <C_V_A_DATE value={row?.updated_at} />
              ) : ["number", "currency"].includes(field?.type) ? (
                isEditing ? (
                  <C_E_NUMBER
                    tempValue={tempValue}
                    onChange={handleChange}
                    onBlur={() => handleBlur(row.id, field.key, value)}
                    onKeyDown={(e) =>
                      handleKeyDown(e, row.id, field.key, value)
                    }
                  />
                ) : (
                  <C_V_NUMBER value={value} />
                )
              ) : field?.type === "year" ? (
                isEditing ? (
                  <C_E_YEAR
                    tempValue={tempValue}
                    onChange={handleChange}
                    onBlur={() => handleBlur(row.id, field.key, value)}
                    onKeyDown={(e) =>
                      handleKeyDown(e, row.id, field.key, value)
                    }
                  />
                ) : (
                  <C_V_YEAR value={value} />
                )
              ) : field?.type === "date" ? (
                isEditing ? (
                  <C_E_DATE
                    tempValue={tempValue}
                    onChange={handleChange}
                    onBlur={() => handleBlur(row.id, field.key, value)}
                    onKeyDown={(e) =>
                      handleKeyDown(e, row.id, field.key, value)
                    }
                  />
                ) : (
                  <C_V_DATE value={value} />
                )
              ) : field?.type === "time" ? (
                isEditing ? (
                  <C_E_TIME
                    tempValue={tempValue}
                    onChange={handleChange}
                    onBlur={() => handleBlur(row.id, field.key, value)}
                    onKeyDown={(e) =>
                      handleKeyDown(e, row.id, field.key, value)
                    }
                  />
                ) : (
                  <C_V_TIME value={value} />
                )
              ) : field?.type === "range" ? (
                isEditing ? (
                  <C_E_RANGE
                    tempValue={tempValue}
                    min={field.min}
                    max={field.max}
                    step={field.step}
                    onDirectSave={(newVal) => {
                      handleSaveData(row.id, field.key, newVal, value);
                      stopEdit();
                    }}
                    onKeyDown={(e) =>
                      handleKeyDown(e, row.id, field.key, value)
                    }
                  />
                ) : (
                  <C_V_RANGE value={value} min={field.min} max={field.max} />
                )
              ) : field?.type === "phone_number" ? (
                isEditing ? (
                  <C_E_PHONE
                    tempValue={tempValue}
                    onChange={handleChange}
                    onBlur={() => handleBlur(row.id, field.key, value)}
                    onKeyDown={(e) =>
                      handleKeyDown(e, row.id, field.key, value)
                    }
                  />
                ) : (
                  <C_V_PHONE value={value} />
                )
              ) : field?.type === "link" ? (
                isEditing ? (
                  <C_E_LINK
                    tempValue={tempValue}
                    onChange={handleChange}
                    onBlur={() => handleBlur(row.id, field.key, value)}
                    onKeyDown={(e) =>
                      handleKeyDown(e, row.id, field.key, value)
                    }
                  />
                ) : (
                  <C_V_LINK value={value} />
                )
              ) : ["kelurahan", "kecamatan", "kota", "provinsi"].includes(
                  field?.type,
                ) ? (
                <C_V_CITY value={value} />
              ) : ["auto_address", "auto_address_poi"].includes(field?.type) ? (
                <C_V_ADDRESS value={value} />
              ) : (
                <C_V_TEXT
                  value={value}
                  tempValue={isEditing ? tempValue : ""}
                  isEditing={isEditing}
                  onChange={handleChange}
                  onBlur={() => handleBlur(row.id, field.key, value)}
                  onKeyDown={(e) => handleKeyDown(e, row.id, field.key, value)}
                />
              )}
            </div>
          );
        })}

        <div
          role="cell"
          className="table_body_spacer_col"
          style={{
            position: "absolute",
            left: totalWidth - 50,
            top: 0,
            height: "100%",
            width: 50,
            margin: 0,
          }}
        />
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.row === nextProps.row &&
      prevProps.absoluteRow === nextProps.absoluteRow &&
      prevProps.activeCol === nextProps.activeCol &&
      prevProps.editingField === nextProps.editingField &&
      prevProps.tempValue === nextProps.tempValue &&
      prevProps.isFeatureSelected === nextProps.isFeatureSelected &&
      prevProps.visibleFields === nextProps.visibleFields
    );
  },
);

export default VirtualTableRow;
